// ============================================================
// TERROR — BOOT SEQUENCE v2 "CINEMATIC CUT"
// Pokemon Crystal x Zelda Minish Cap style title cinematic.
// Eight beats, all skippable. Loops back as attract mode.
//
//   Beat 1   — Terror studio bumper + comet spiral   (~3s)
//              (Gold/Silver homage: star spirals into the logo)
//   Beat 2   — LORE PROLOGUE — stained-glass tapestry (~9.5s)
//              (Minish Cap homage: illuminated storybook lines)
//   Beat 3   — Krush diamond crushes in               (~6.5s)  black & white
//   Beat 4   — Void awakens, ilk glyphs utter energy  (~5s)
//              (Crystal homage: Unown-style glyph reveals)
//   Beat 5   — THE DASH — J'ynxi blurs across screen  (~2.8s)
//              (Crystal homage: the Suicune run)
//   Beat 6   — Legendary rises, eyes ignite           (~4.2s)
//   Beat 7   — Title card + god rays + ghost legend
//              (Crystal homage: legendary looms UNDER the logo)
//   Beat 8   — Press enter (attract loop after 30s idle)
//
//   CINEMATIC LAYER (always on during beats 1–6):
//     • Letterbox bars  — slide in at boot, retract at title
//     • Film grain      — static SVG noise, whisper-subtle
//     • Vignette        — radial darkening at edges
//
// ──────────────────────────────────────────────────────────────
//  ASSET UPLOAD GUIDE — read this first
// ──────────────────────────────────────────────────────────────
//
//  Every asset for the boot sequence is listed in BOOT_ASSETS
//  below. Each entry has:
//
//    • a constant name (don't change this — code references it)
//    • a CDN URL (this is what you swap)
//    • a short note describing what to make
//
//  TO SWAP IN A REAL ASSET:
//    1. Make the file (.webp for images, .mp3 for audio)
//    2. Drop it into:  C:\Users\9092a\Desktop\terror-assets\
//    3. Upload via Wrangler to terror-assets.pages.dev
//       (use the same workflow you use for cards)
//    4. Edit ONE line below — change `null` to the filename
//       (just the filename, not the full URL — CDN base is
//       added automatically by the cdnUrl() helper)
//
//  EXAMPLE:
//    Before:  legendarySilhouette: null,
//    After:   legendarySilhouette: "boot_legendary.webp",
//
//  AUDIO FORMAT:
//    • Use .mp3 — works in every browser, no transcoding needed
//    • Stereo, 44.1 kHz, 128–192 kbps is plenty for web
//    • Loop length: 30–90 seconds for the title theme
//    • Keep file size under ~2 MB so it loads fast on mobile
//
//  IMAGE FORMAT:
//    • .webp always (per build guide)
//    • Studio bumper:         ~600x300 px, transparent bg
//    • Prologue tapestry:     ~700x500 px, stained-glass art
//    • Krush wordmark:        ~600x500 px, transparent bg
//                             (diamond-shaped "Krush", B&W)
//    • Legendary silhouette:  ~600x800 px, transparent bg
//                             (used for BOTH the dash and the rise)
//    • Title wordmark:        ~800x400 px, transparent bg
//    • Mascot carousel:       ~400x400 px each, transparent bg
//
//  AUDIO ASSETS:
//    • bootMusicUrl   — main title theme that loops
//    • bootChimeUrl   — short stinger when title appears
//      (optional — leave null and you get silence)
//
//  LORE PROLOGUE TEXT:
//    The storybook lines live in LORE_LINES below — edit the
//    strings freely, timing recalculates around them. Keep it
//    to 3 lines for the current pacing.
//
//  TESTING WITH PLACEHOLDERS:
//    All assets default to null. The component renders
//    CSS/SVG-drawn placeholders in your existing GM/SD palette
//    (and B&W for the Krush scene).
//    Choreography works perfectly without any uploads.
//    Ship the boot screen first, swap art in over time.
// ============================================================

import { useEffect, useRef, useState } from "react";
import { SD } from "./theme";
import { CornerFlourish, OrnateDivider, StarDust } from "./TerrorUI";
import { cdnUrl } from "./CDN";

// ── Grimoire Manuscript Palette (matches MainMenu) ───────────
const GM = {
  page:          "#0f0c07",
  pageWarm:      "#1a1509",
  pageMid:       "#241c0c",
  inkGold:       "#c8963c",
  inkGoldBright: "#e8b84b",
  inkBrown:      "#8a6a30",
  inkRed:        "#8b2020",
  vellum:        "#e8d5a3",
  vellumDim:     "#b89a5a",
  vellumFaint:   "#7a6040",
  font: "'Cinzel', 'Palatino Linotype', serif",
};

// ════════════════════════════════════════════════════════════
//  ASSET MANIFEST — swap null → "filename.webp" to replace
// ════════════════════════════════════════════════════════════
const BOOT_ASSETS = {
  // Beat 1 — Terror studio bumper
  studioWordmark:      null,  // e.g. "boot_terror_logo.webp"

  // Beat 2 — Lore prologue tapestry (optional full-art panel;
  // if null, the SVG stained-glass window placeholder renders)
  prologueArt:         null,  // e.g. "boot_prologue.webp"

  // Beat 3 — Krush diamond wordmark
  krushWordmark:       null,  // e.g. "boot_krush.webp"

  // Beats 5+6+7 — Legendary silhouette (dash, rise, and title ghost)
  legendarySilhouette: null,  // e.g. "boot_legendary.webp"

  // Beat 7 — TERROR title wordmark
  titleWordmark:       null,  // e.g. "boot_title.webp"

  // Beat 7/8 — Mascot carousel (cycles like Red/Blue's Pokemon next to logo)
  mascotCarousel: [
    null,  // e.g. "boot_mascot_h01.webp"
    null,  // e.g. "boot_mascot_h09.webp"
    null,  // e.g. "boot_mascot_xulan.webp"
    null,  // e.g. "boot_mascot_spectral_tiger.webp"
    null,  // e.g. "boot_mascot_shadow_fox.webp"
    null,  // e.g. "boot_mascot_inverted_serpent.webp"
  ],

  // Audio — both optional, set to null for silent boot
  bootMusicUrl: null,  // e.g. "boot_theme.mp3"  (looping title theme)
  bootChimeUrl: null,  // e.g. "boot_chime.mp3"  (one-shot when title appears)
};

// Resolve filename → full URL (or return null for placeholder)
const resolve = (name) => name ? cdnUrl(name) : null;

// ════════════════════════════════════════════════════════════
//  LORE PROLOGUE — edit these lines freely
//  delay = seconds into the prologue beat when the line fades in
// ════════════════════════════════════════════════════════════
const LORE_LINES = [
  { text: "before the first Solrend was ever struck,",  delay: 0.5 },
  { text: "the Unstable World cracked open —",          delay: 2.7 },
  { text: "and four ilks spilled into the dark.",       delay: 4.9 },
];
// The four sigils in the stained glass ignite after line 3:
const SIGIL_IGNITE_AT = 5.6;   // seconds into prologue
const PROLOGUE_FADE_AT = 8.4;  // whole panel begins fading out

// ════════════════════════════════════════════════════════════
//  TIMING — tweak these to taste
// ════════════════════════════════════════════════════════════
const TIMING = {
  studio:     3000,   // Beat 1 — Terror studio bumper + comet
  prologue:   9500,   // Beat 2 — stained-glass lore lines
  krush:      6500,   // Beat 3 — narration → shatter → Krush reforms
  voidWake:   5000,   // Beat 4 — starfield + glyphs utter energy
  dash:       2800,   // Beat 5 — legendary blurs across the screen
  legendary:  4200,   // Beat 6 — silhouette rises, eyes ignite
  // Beat 7 (title appear) is instant — handled by CSS
  // Beat 8 (press enter loop) — waits for input, replays after idle
  attractIdle: 30000, // Replay cinematic after 30s idle
  mascotCycle: 4000,  // Mascot beside title swaps every N ms
};

// ════════════════════════════════════════════════════════════
//  KEYFRAMES — injected imperatively to document.head
//  (per known-traps: never gate keyframes behind conditionally
//   rendered <style> blocks — they vanish when the beat unmounts)
// ════════════════════════════════════════════════════════════
function ensureKeyframes(id, css) {
  if (typeof document === "undefined") return;
  if (document.getElementById(id)) return;
  const el = document.createElement("style");
  el.id = id;
  el.textContent = css;
  document.head.appendChild(el);
}

const BOOT_KEYFRAMES = `
  /* ── shared ─────────────────────────────────────────── */
  @keyframes bootFadeIn  { from { opacity: 0 } to { opacity: 1 } }
  @keyframes bootFadeOut { from { opacity: 1 } to { opacity: 0 } }
  @keyframes bootRise    { from { opacity: 0; transform: translateY(40px) scale(0.95) } to { opacity: 1; transform: translateY(0) scale(1) } }
  @keyframes bootBreathe { 0%,100% { transform: scale(1) translateY(0) } 50% { transform: scale(1.02) translateY(-6px) } }
  @keyframes bootPulse   { 0%,100% { opacity: 0.45 } 50% { opacity: 1 } }
  @keyframes grainFlicker{ 0%,100% { opacity: 0.045 } 50% { opacity: 0.06 } }

  /* ── beat 1: studio ─────────────────────────────────── */
  @keyframes jynxiEyesPulse {
    0%   { opacity: 0; transform: scale(1.15); }
    25%  { opacity: 0.85; transform: scale(1); }
    60%  { opacity: 0.85; transform: scale(1); }
    100% { opacity: 0; transform: scale(0.95); }
  }
  @keyframes cometSpiralFallback {
    0%   { transform: rotate(0deg)    translateX(240px) scale(1);   opacity: 0; }
    10%  { opacity: 1; }
    85%  { opacity: 1; }
    100% { transform: rotate(720deg)  translateX(0px)   scale(0.2); opacity: 0; }
  }
  @keyframes cometBurst {
    0%   { opacity: 0; transform: scale(0.2); }
    35%  { opacity: 0.9; transform: scale(1.15); }
    100% { opacity: 0; transform: scale(1.6); }
  }

  /* ── beat 2: prologue ───────────────────────────────── */
  @keyframes loreLineIn {
    0%   { opacity: 0; transform: translateY(10px); filter: blur(3px); }
    100% { opacity: 1; transform: translateY(0);   filter: blur(0); }
  }
  @keyframes candleFlicker {
    0%,100% { opacity: 0.92; } 22% { opacity: 1; } 47% { opacity: 0.88; }
    68% { opacity: 0.97; } 84% { opacity: 0.9; }
  }
  @keyframes paneIgnite {
    0%   { opacity: 0.12; filter: drop-shadow(0 0 0px transparent); }
    60%  { opacity: 1;    filter: drop-shadow(0 0 22px currentColor); }
    100% { opacity: 0.9;  filter: drop-shadow(0 0 12px currentColor); }
  }
  @keyframes prologueFadeOut {
    0% { opacity: 1; } 100% { opacity: 0; transform: scale(1.03); filter: blur(3px); }
  }

  /* ── beat 3: krush ──────────────────────────────────── */
  @keyframes krushSwirl {
    0%   { transform: rotate(0deg) scale(0.3); opacity: 0; }
    50%  { transform: rotate(180deg) scale(1.1); opacity: 0.85; }
    100% { transform: rotate(360deg) scale(1); opacity: 0.7; }
  }
  @keyframes krushDripFall {
    0%   { transform: translateY(-30px); opacity: 0; }
    30%  { opacity: 1; }
    100% { transform: translateY(60vh); opacity: 0; }
  }
  @keyframes narrationWordIn {
    0%   { opacity: 0; transform: translateY(8px); filter: blur(4px); }
    100% { opacity: 1; transform: translateY(0); filter: blur(0); }
  }
  @keyframes narrationShatter {
    0%   { opacity: 1; transform: translate(0,0) rotate(0deg) scale(1); filter: blur(0); }
    100% { opacity: 0; transform: translate(var(--shx, 80px), var(--shy, -40px)) rotate(var(--shr, 25deg)) scale(0.6); filter: blur(8px); }
  }
  @keyframes krushFlash {
    0%   { opacity: 0; }
    30%  { opacity: 1; }
    100% { opacity: 0; }
  }
  @keyframes krushCrackWeb {
    0%   { opacity: 0; transform: scale(0.4); }
    40%  { opacity: 1; transform: scale(1); }
    100% { opacity: 0; transform: scale(1.05); }
  }
  @keyframes krushSlamIn {
    0%   { transform: scale(3) rotate(-12deg); opacity: 0; filter: blur(20px); }
    60%  { transform: scale(0.92) rotate(2deg); opacity: 1; filter: blur(0px); }
    80%  { transform: scale(1.04) rotate(-1deg); opacity: 1; filter: blur(0px); }
    100% { transform: scale(1) rotate(0deg); opacity: 1; filter: blur(0px); }
  }
  @keyframes krushShake {
    0%, 100% { transform: translate(0,0); }
    20% { transform: translate(-3px, 2px); }
    40% { transform: translate(3px, -1px); }
    60% { transform: translate(-2px, -2px); }
    80% { transform: translate(2px, 1px); }
  }
  @keyframes krushCamShake {
    0%, 100% { transform: translate(0,0); }
    15% { transform: translate(-6px, 4px); }
    30% { transform: translate(5px, -3px); }
    45% { transform: translate(-4px, -4px); }
    60% { transform: translate(4px, 3px); }
    80% { transform: translate(-2px, 1px); }
  }
  @keyframes shardEmber {
    0%   { opacity: 0; transform: translate(0, 0) rotate(0deg); }
    15%  { opacity: 0.9; }
    100% { opacity: 0; transform: translate(var(--ex, 40px), var(--ey, -120px)) rotate(var(--er, 180deg)); }
  }

  /* ── beat 4: void / glyphs ──────────────────────────── */
  @keyframes ilkIgnite {
    0% { opacity: 0; transform: scale(0.5) } 60% { opacity: 1; transform: scale(1.3) } 100% { opacity: 0.7; transform: scale(1) }
  }
  @keyframes glyphUtter {
    0%   { opacity: 0.9; transform: scale(0.3); }
    100% { opacity: 0;   transform: scale(2.6); }
  }
  @keyframes shootingStar {
    0%   { transform: translate(-15vw, -10vh) rotate(18deg); opacity: 0; }
    10%  { opacity: 1; }
    60%  { opacity: 1; }
    100% { transform: translate(115vw, 45vh) rotate(18deg); opacity: 0; }
  }

  /* ── beat 5: the dash ───────────────────────────────── */
  @keyframes legendaryDash {
    0%   { transform: translateX(-70vw) scaleX(-1) scale(0.55) rotate(6deg); opacity: 0; }
    8%   { opacity: 1; }
    92%  { opacity: 1; }
    100% { transform: translateX(80vw)  scaleX(-1) scale(0.55) rotate(6deg); opacity: 0; }
  }
  @keyframes speedLine {
    0%   { transform: translateX(110vw); opacity: 0; }
    15%  { opacity: 0.7; }
    100% { transform: translateX(-120vw); opacity: 0; }
  }
  @keyframes sigilScatter {
    0%   { opacity: 0.8; transform: translate(0,0) scale(1); }
    100% { opacity: 0;   transform: translate(var(--sx, 60px), var(--sy, -40px)) scale(0.4); }
  }
  @keyframes dashEyesFlash {
    0%   { opacity: 0; transform: scale(1.2); }
    30%  { opacity: 0.9; transform: scale(1); }
    70%  { opacity: 0.9; }
    100% { opacity: 0; transform: scale(0.96); }
  }

  /* ── beat 6: legendary rise ─────────────────────────── */
  @keyframes eyeIgniteFlash {
    0%   { opacity: 0; }
    20%  { opacity: 0.55; }
    100% { opacity: 0; }
  }
  @keyframes fogDrift {
    0%   { transform: translateX(-6%) scale(1); }
    50%  { transform: translateX(6%)  scale(1.06); }
    100% { transform: translateX(-6%) scale(1); }
  }
  @keyframes voidLightning {
    0%, 88%, 100% { opacity: 0; }
    89% { opacity: 0.25; } 90% { opacity: 0.05; }
    91% { opacity: 0.35; } 93% { opacity: 0; }
  }

  /* ── beat 7: title ──────────────────────────────────── */
  @keyframes godRaysSpin {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }
  @keyframes titleShockwave {
    0%   { opacity: 0.8; transform: scale(0.2); }
    100% { opacity: 0;   transform: scale(2.4); }
  }
  @keyframes ghostLoom {
    0%,100% { opacity: 0.10; transform: translateY(0)    scale(1); }
    50%     { opacity: 0.16; transform: translateY(-8px) scale(1.015); }
  }
  @keyframes goldShimmer { 0%,100% { background-position: 0% 50% } 50% { background-position: 100% 50% } }
`;

// ════════════════════════════════════════════════════════════
//  AUDIO HOOK — plays music if URL provided, silent otherwise
// ════════════════════════════════════════════════════════════
function useBootAudio() {
  const musicRef = useRef(null);
  const chimeRef = useRef(null);

  useEffect(() => {
    const musicUrl = resolve(BOOT_ASSETS.bootMusicUrl);
    const chimeUrl = resolve(BOOT_ASSETS.bootChimeUrl);

    if (musicUrl) {
      const a = new Audio(musicUrl);
      a.loop = true;
      a.volume = 0.5;
      musicRef.current = a;
    }
    if (chimeUrl) {
      chimeRef.current = new Audio(chimeUrl);
      chimeRef.current.volume = 0.7;
    }
    return () => {
      if (musicRef.current) { musicRef.current.pause(); musicRef.current = null; }
      if (chimeRef.current) { chimeRef.current.pause(); chimeRef.current = null; }
    };
  }, []);

  // Browsers block autoplay until user interacts — we start music
  // on the first user gesture (any keypress / tap)
  const startMusic = () => {
    if (musicRef.current) musicRef.current.play().catch(() => {});
  };
  const playChime = () => {
    if (chimeRef.current) {
      chimeRef.current.currentTime = 0;
      chimeRef.current.play().catch(() => {});
    }
  };
  return { startMusic, playChime };
}

// ════════════════════════════════════════════════════════════
//  CINEMATIC LAYER COMPONENTS
//  Letterbox bars, film grain, vignette — always rendered,
//  they're what makes it feel like a film and not a screen.
// ════════════════════════════════════════════════════════════

// ── Letterbox bars — slide in on boot, retract at the title ──
function Letterbox({ retracted }) {
  const bar = (pos) => ({
    position: "absolute",
    left: 0, right: 0,
    [pos]: 0,
    height: retracted ? 0 : "7vh",
    background: "#000",
    zIndex: 50,
    transition: "height 1.4s cubic-bezier(0.6, 0, 0.2, 1)",
    pointerEvents: "none",
    boxShadow: retracted ? "none" : `0 0 24px rgba(0,0,0,0.8)`,
  });
  return (
    <>
      <div style={bar("top")}/>
      <div style={bar("bottom")}/>
    </>
  );
}

// ── Film grain — one static SVG noise pass, whisper subtle ──
// Static turbulence (no SMIL) keeps mobile GPUs happy; a slow
// CSS opacity flicker sells the "projector" feel for free.
function FilmGrain() {
  return (
    <svg width="100%" height="100%" style={{
      position: "absolute",
      inset: 0,
      zIndex: 48,
      pointerEvents: "none",
      mixBlendMode: "overlay",
      animation: "grainFlicker 1.8s steps(2) infinite",
    }}>
      <filter id="bootGrain">
        <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" stitchTiles="stitch"/>
        <feColorMatrix type="saturate" values="0"/>
      </filter>
      <rect width="100%" height="100%" filter="url(#bootGrain)"/>
    </svg>
  );
}

// ── Vignette — darkened edges, always on ─────────────────────
function Vignette() {
  return (
    <div style={{
      position: "absolute",
      inset: 0,
      zIndex: 47,
      pointerEvents: "none",
      background: "radial-gradient(ellipse at 50% 50%, transparent 55%, rgba(0,0,0,0.55) 100%)",
    }}/>
  );
}

// ════════════════════════════════════════════════════════════
//  PLACEHOLDER COMPONENTS
//  Used when an asset is null — render CSS-only stand-ins so
//  the choreography is visible without any uploaded art.
// ════════════════════════════════════════════════════════════
function PlaceholderWordmark({ text, size = 64, color = GM.inkGoldBright, glow = true }) {
  return (
    <div style={{
      fontFamily: GM.font,
      fontSize: size,
      fontWeight: 900,
      fontStyle: "italic",
      letterSpacing: 6,
      background: `linear-gradient(135deg, ${color}, ${GM.inkGold})`,
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      textShadow: glow ? `0 0 30px ${color}66` : "none",
      filter: glow ? `drop-shadow(0 0 20px ${color}55)` : "none",
    }}>
      {text}
    </div>
  );
}

// ── J'ynxi Eyes — angular triangular eyes ─────────────────────
// Two sharp triangular eyes with curved swoop "nose-bridge" lines
// that loop down into the eye, plus an oval iris below.
// Used in the studio bumper AND as the "something saw you" flash
// at the end of the dash beat.
function JynxiEyes({ size = 280, color = "#fff", opacity = 1 }) {
  return (
    <svg width={size * 2.4} height={size} viewBox="0 0 600 250" style={{
      opacity,
      filter: `drop-shadow(0 0 20px ${color}55) drop-shadow(0 0 6px ${color}88)`,
    }}>
      {/* LEFT EYE */}
      <g>
        <path d="M 30 230 L 200 30 L 270 220 Z"
              fill="none" stroke={color} strokeWidth="6" strokeLinejoin="round"/>
        <path d="M 175 60 Q 200 130 130 170 Q 110 175 105 165"
              fill="none" stroke={color} strokeWidth="5" strokeLinecap="round"/>
        <path d="M 75 215 Q 100 165 175 165 Q 240 165 250 215 Z"
              fill={color}/>
        <ellipse cx="180" cy="190" rx="16" ry="22" fill="#000"/>
      </g>
      {/* RIGHT EYE — mirrored */}
      <g transform="translate(600, 0) scale(-1, 1)">
        <path d="M 30 230 L 200 30 L 270 220 Z"
              fill="none" stroke={color} strokeWidth="6" strokeLinejoin="round"/>
        <path d="M 175 60 Q 200 130 130 170 Q 110 175 105 165"
              fill="none" stroke={color} strokeWidth="5" strokeLinecap="round"/>
        <path d="M 75 215 Q 100 165 175 165 Q 240 165 250 215 Z"
              fill={color}/>
        <ellipse cx="180" cy="190" rx="16" ry="22" fill="#000"/>
      </g>
    </svg>
  );
}

function PlaceholderLegendary({ size = 320 }) {
  // J'ynxi silhouette — crowned/horned head, triangular face,
  // lightning-bolt body, side curl ornaments. Solid dark fill
  // with a faint edge glow so it reads as a true silhouette.
  return (
    <svg width={size} height={size * 1.45} viewBox="0 0 300 435" style={{
      filter: `drop-shadow(0 0 40px ${SD.purple}88) drop-shadow(0 0 20px ${GM.inkGold}55)`,
    }}>
      <defs>
        <radialGradient id="jynxiBody" cx="50%" cy="50%">
          <stop offset="0%"   stopColor="#1a0a2e" />
          <stop offset="60%"  stopColor="#07071a" />
          <stop offset="100%" stopColor="#000000" />
        </radialGradient>
        <radialGradient id="jynxiEyeGlow" cx="50%" cy="50%">
          <stop offset="0%"   stopColor="#f0d060" />
          <stop offset="60%"  stopColor="#c8963c" />
          <stop offset="100%" stopColor="#8a6520" />
        </radialGradient>
      </defs>

      {/* ── HEAD — crowned shield with three peaks (horns/crown) ── */}
      <path d="
        M 80 100
        L 100 50
        L 120 90
        L 150 30
        L 180 90
        L 200 50
        L 220 100
        L 240 200
        L 200 230
        L 100 230
        L 60 200
        Z
      " fill="url(#jynxiBody)" />

      {/* Inner triangular face mark (the diamond/hood pattern area) */}
      <path d="M 150 90 L 215 215 L 85 215 Z" fill="#000" opacity="0.55"/>

      {/* ── EYES — small glowing slits ── */}
      <ellipse cx="120" cy="160" rx="14" ry="6" fill="url(#jynxiEyeGlow)" opacity="0.7">
        <animate attributeName="opacity" values="0.4;0.9;0.4" dur="2.4s" repeatCount="indefinite"/>
      </ellipse>
      <ellipse cx="180" cy="160" rx="14" ry="6" fill="url(#jynxiEyeGlow)" opacity="0.7">
        <animate attributeName="opacity" values="0.4;0.9;0.4" dur="2.4s" repeatCount="indefinite"/>
      </ellipse>

      {/* ── SIDE CURL ORNAMENTS (those flowing horn-curls) ── */}
      <path d="
        M 60 210
        Q 30 220 25 250
        Q 22 285 50 300
        Q 70 305 75 285
        Q 60 290 55 270
        Q 55 240 80 235
        Z
      " fill="url(#jynxiBody)" />
      <path d="
        M 240 210
        Q 270 220 275 250
        Q 278 285 250 300
        Q 230 305 225 285
        Q 240 290 245 270
        Q 245 240 220 235
        Z
      " fill="url(#jynxiBody)" />

      {/* ── BODY — lightning-bolt / dagger shape pointing down ── */}
      <path d="
        M 100 230
        L 200 230
        L 215 270
        L 175 280
        L 195 320
        L 165 330
        L 180 380
        L 150 425
        L 120 380
        L 135 330
        L 105 320
        L 125 280
        L 85 270
        Z
      " fill="url(#jynxiBody)" />

      {/* ── CHEST SIGIL — pulsing diamond (the IE diamond mark) ── */}
      <g transform="translate(150 290)">
        <polygon points="0,-14 11,0 0,14 -11,0"
                 fill="none" stroke={GM.inkGold} strokeWidth="1.5" opacity="0.7">
          <animate attributeName="opacity" values="0.3;0.9;0.3" dur="3s" repeatCount="indefinite"/>
        </polygon>
        <polygon points="0,-7 5,0 0,7 -5,0"
                 fill={GM.inkGoldBright} opacity="0.5">
          <animate attributeName="opacity" values="0.2;0.7;0.2" dur="3s" repeatCount="indefinite"/>
        </polygon>
      </g>
    </svg>
  );
}

function PlaceholderMascot({ size = 180, idx = 0 }) {
  // Cycle through different placeholder colors per mascot index
  const palettes = [
    { a: SD.red,     b: SD.redDim },       // dragon-like
    { a: SD.purple,  b: SD.purpleDim },    // phantom-like
    { a: SD.teal,    b: SD.tealDim },      // space-like
    { a: SD.gold,    b: SD.goldDim },      // time-like
    { a: SD.blue,    b: SD.blueDim },
    { a: "#7a4a2a", b: "#3a2010" },
  ];
  const p = palettes[idx % palettes.length];
  return (
    <svg width={size} height={size} viewBox="0 0 200 200" style={{
      filter: `drop-shadow(0 0 16px ${p.a}88)`,
    }}>
      <defs>
        <radialGradient id={`mascot${idx}`} cx="50%" cy="50%">
          <stop offset="0%"   stopColor={p.a} />
          <stop offset="100%" stopColor={p.b} />
        </radialGradient>
      </defs>
      <ellipse cx="100" cy="120" rx="60" ry="55" fill={`url(#mascot${idx})`}/>
      <ellipse cx="100" cy="80"  rx="35" ry="38" fill={`url(#mascot${idx})`}/>
      <circle cx="88"  cy="78" r="4" fill="#000" opacity="0.8"/>
      <circle cx="112" cy="78" r="4" fill="#000" opacity="0.8"/>
      <circle cx="100" cy="100" r="85" fill="none" stroke={p.a} strokeWidth="1" opacity="0.3"/>
    </svg>
  );
}

// ── Stained-glass prologue window — the Minish Cap tapestry ──
// A gothic arched window with four diamond panes, one per ilk.
// Panes sit dim until SIGIL_IGNITE_AT, then light up in sequence.
function StainedGlassWindow({ size = 340 }) {
  const panes = [
    { x: 150, y: 105, color: SD.teal,   char: "◇", delay: 0 },     // Space
    { x: 95,  y: 175, color: SD.purple, char: "◈", delay: 0.35 },  // Phantom
    { x: 205, y: 175, color: SD.red,    char: "◆", delay: 0.7 },   // Dragon
    { x: 150, y: 245, color: SD.gold,   char: "✦", delay: 1.05 },  // Time
  ];
  return (
    <svg width={size} height={size * 1.25} viewBox="0 0 300 375" style={{
      animation: "candleFlicker 4.2s ease-in-out infinite",
    }}>
      <defs>
        <linearGradient id="glassSheen" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%"  stopColor="#fff" stopOpacity="0.10"/>
          <stop offset="50%" stopColor="#fff" stopOpacity="0.02"/>
          <stop offset="100%" stopColor="#fff" stopOpacity="0.08"/>
        </linearGradient>
      </defs>

      {/* Gothic arch frame */}
      <path d="M 40 360 L 40 140 Q 40 30 150 15 Q 260 30 260 140 L 260 360 Z"
            fill={GM.pageWarm} stroke={GM.inkGold} strokeWidth="4"/>
      <path d="M 52 352 L 52 145 Q 52 45 150 30 Q 248 45 248 145 L 248 352 Z"
            fill="none" stroke={GM.inkBrown} strokeWidth="1.5" opacity="0.8"/>

      {/* Leadwork — diamond lattice hints */}
      <line x1="150" y1="30" x2="150" y2="352" stroke={GM.inkBrown} strokeWidth="1" opacity="0.45"/>
      <line x1="52"  y1="175" x2="248" y2="175" stroke={GM.inkBrown} strokeWidth="1" opacity="0.45"/>
      <line x1="52"  y1="260" x2="248" y2="260" stroke={GM.inkBrown} strokeWidth="1" opacity="0.35"/>

      {/* Glass sheen */}
      <path d="M 52 352 L 52 145 Q 52 45 150 30 Q 248 45 248 145 L 248 352 Z"
            fill="url(#glassSheen)"/>

      {/* Four ilk panes — diamond frames + sigils */}
      {panes.map((p, i) => (
        <g key={i} style={{
          color: p.color,
          animation: `paneIgnite 1.1s ease-out ${SIGIL_IGNITE_AT + p.delay}s both`,
        }}>
          <polygon
            points={`${p.x},${p.y - 34} ${p.x + 28},${p.y} ${p.x},${p.y + 34} ${p.x - 28},${p.y}`}
            fill={`${p.color}22`} stroke={p.color} strokeWidth="2"/>
          <text x={p.x} y={p.y + 9} textAnchor="middle" fontSize="26"
                fill={p.color} style={{ fontFamily: "serif" }}>
            {p.char}
          </text>
        </g>
      ))}

      {/* Sill ornament */}
      <rect x="30" y="358" width="240" height="8" fill={GM.inkGold} opacity="0.7" rx="2"/>
      <rect x="42" y="366" width="216" height="4" fill={GM.inkBrown} opacity="0.6" rx="2"/>
    </svg>
  );
}

// ════════════════════════════════════════════════════════════
//  THE BOOT SEQUENCE COMPONENT
// ════════════════════════════════════════════════════════════
export default function BootSequence({ onComplete }) {
  // Phase: 'studio' | 'prologue' | 'krush' | 'void' | 'dash'
  //      | 'legendary' | 'title' | 'pressEnter'
  const [phase, setPhase] = useState("studio");
  const [mascotIdx, setMascotIdx] = useState(0);
  const idleTimerRef = useRef(null);
  const audio = useBootAudio();

  // Inject keyframes once — imperative, keyed off DOM node
  // existence (known-traps: keyframe injection trap)
  ensureKeyframes("terror-boot-keyframes", BOOT_KEYFRAMES);

  // ── Phase progression ──────────────────────────────────────
  useEffect(() => {
    if (phase === "studio") {
      const t = setTimeout(() => setPhase("prologue"), TIMING.studio);
      return () => clearTimeout(t);
    }
    if (phase === "prologue") {
      const t = setTimeout(() => setPhase("krush"), TIMING.prologue);
      return () => clearTimeout(t);
    }
    if (phase === "krush") {
      const t = setTimeout(() => setPhase("void"), TIMING.krush);
      return () => clearTimeout(t);
    }
    if (phase === "void") {
      const t = setTimeout(() => setPhase("dash"), TIMING.voidWake);
      return () => clearTimeout(t);
    }
    if (phase === "dash") {
      const t = setTimeout(() => setPhase("legendary"), TIMING.dash);
      return () => clearTimeout(t);
    }
    if (phase === "legendary") {
      const t = setTimeout(() => {
        setPhase("title");
        audio.playChime();
      }, TIMING.legendary);
      return () => clearTimeout(t);
    }
    if (phase === "title") {
      // Title appears, then we slide into pressEnter after 1.5s
      const t = setTimeout(() => setPhase("pressEnter"), 1500);
      return () => clearTimeout(t);
    }
    // pressEnter — waits for input, no auto-advance
    // (attract-mode replay handled separately below)
  }, [phase, audio]);

  // ── Mascot carousel — cycle while on title/pressEnter ──────
  useEffect(() => {
    if (phase !== "title" && phase !== "pressEnter") return;
    const i = setInterval(() => {
      setMascotIdx(x => (x + 1) % BOOT_ASSETS.mascotCarousel.length);
    }, TIMING.mascotCycle);
    return () => clearInterval(i);
  }, [phase]);

  // ── Attract-mode loop — replay if idle on pressEnter ───────
  useEffect(() => {
    if (phase !== "pressEnter") return;
    idleTimerRef.current = setTimeout(() => {
      // Replay from the lore prologue (skip the studio bumper) —
      // idle onlookers get the STORY, Twilight Princess style
      setPhase("prologue");
    }, TIMING.attractIdle);
    return () => clearTimeout(idleTimerRef.current);
  }, [phase]);

  // ── Input handlers — skip / advance ────────────────────────
  useEffect(() => {
    const advance = () => {
      audio.startMusic();  // First gesture starts music
      if (phase === "studio")    { setPhase("prologue");   return; }
      if (phase === "prologue")  { setPhase("krush");      return; }
      if (phase === "krush")     { setPhase("void");       return; }
      if (phase === "void")      { setPhase("dash");       return; }
      if (phase === "dash")      { setPhase("legendary");  return; }
      if (phase === "legendary") { setPhase("title");      audio.playChime(); return; }
      if (phase === "title")     { setPhase("pressEnter"); return; }
      if (phase === "pressEnter") {
        clearTimeout(idleTimerRef.current);
        onComplete?.();
      }
    };
    const onKey = (e) => {
      // Enter / Space / any letter advances. Escape skips entire boot.
      if (e.key === "Escape") { onComplete?.(); return; }
      if (e.key === "Enter" || e.key === " " || e.key.length === 1) advance();
    };
    const onClick = () => advance();
    window.addEventListener("keydown", onKey);
    window.addEventListener("click", onClick);
    window.addEventListener("touchstart", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("click", onClick);
      window.removeEventListener("touchstart", onClick);
    };
  }, [phase, audio, onComplete]);

  const isTitlePhase = phase === "title" || phase === "pressEnter";

  // ────────────────────────────────────────────────────────────
  //  RENDER
  // ────────────────────────────────────────────────────────────
  return (
    <div style={{
      position: "fixed",
      inset: 0,
      background: GM.page,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      overflow: "hidden",
      fontFamily: GM.font,
      cursor: "pointer",
      userSelect: "none",
      zIndex: 9999,
    }}>

      {/* ═══════════ BEAT 1 — TERROR STUDIO BUMPER ═══════════ */}
      {phase === "studio" && (
        <div style={{
          position: "relative",
          textAlign: "center",
          animation: "bootFadeIn 0.8s ease-out",
        }}>
          {/* Comet spiral — Gold/Silver homage: a star spirals in
              and dies into the wordmark as the eyes fade up */}
          <div style={{
            position: "absolute",
            top: "50%", left: "50%",
            width: 0, height: 0,
            zIndex: 2,
            pointerEvents: "none",
          }}>
            <div style={{
              position: "absolute",
              width: 7, height: 7,
              marginLeft: -3.5, marginTop: -3.5,
              borderRadius: "50%",
              background: "#fff",
              boxShadow: `0 0 12px #fff, 0 0 28px ${GM.inkGoldBright}, 0 0 46px ${GM.inkGold}`,
              transformOrigin: "0 0",
              animation: "cometSpiralFallback 1.6s cubic-bezier(0.4, 0, 0.6, 1) 0.1s forwards",
            }}/>
            {/* Burst where the comet dies */}
            <div style={{
              position: "absolute",
              width: 90, height: 90,
              marginLeft: -45, marginTop: -45,
              borderRadius: "50%",
              background: `radial-gradient(circle, ${GM.inkGoldBright}55 0%, transparent 70%)`,
              opacity: 0,
              animation: "cometBurst 0.7s ease-out 1.6s forwards",
            }}/>
          </div>

          {/* J'ynxi eyes — fade in behind, hold, fade out */}
          <div style={{
            position: "absolute",
            top: "50%", left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 0,
            pointerEvents: "none",
            animation: `jynxiEyesPulse ${TIMING.studio}ms ease-in-out forwards`,
          }}>
            <JynxiEyes size={180} color="#fff"/>
          </div>

          {/* Wordmark + subtitle layered above */}
          <div style={{ position: "relative", zIndex: 1 }}>
            <div style={{
              height: 1,
              width: 200,
              margin: "0 auto 24px",
              background: `linear-gradient(90deg, transparent, ${GM.inkGold}, transparent)`,
            }}/>

            {resolve(BOOT_ASSETS.studioWordmark) ? (
              <img src={resolve(BOOT_ASSETS.studioWordmark)} alt="Terror"
                   style={{ maxWidth: 400, opacity: 0.95 }}/>
            ) : (
              <PlaceholderWordmark text="TERROR" size={36} glow={false}/>
            )}

            <div style={{
              marginTop: 14,
              fontSize: 10,
              letterSpacing: 8,
              color: GM.vellumFaint,
              fontStyle: "italic",
            }}>
              an illegal empire production
            </div>

            <div style={{
              height: 1,
              width: 200,
              margin: "24px auto 0",
              background: `linear-gradient(90deg, transparent, ${GM.inkGold}, transparent)`,
            }}/>
          </div>
        </div>
      )}

      {/* ═══════════ BEAT 2 — LORE PROLOGUE ═══════════ */}
      {/* Minish Cap homage: illuminated storybook lines over a
          stained-glass window. Panes ignite as line 3 lands. */}
      {phase === "prologue" && (
        <div style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 26,
          background: `
            radial-gradient(ellipse at 50% 30%, ${GM.pageMid} 0%, ${GM.page} 70%),
            ${GM.page}
          `,
          animation: `
            bootFadeIn 1.1s ease-out,
            prologueFadeOut 1.1s ease-in ${PROLOGUE_FADE_AT}s forwards
          `,
          padding: "0 24px",
        }}>
          {/* Warm candle pool behind the window */}
          <div style={{
            position: "absolute",
            top: "50%", left: "50%",
            transform: "translate(-50%, -55%)",
            width: 560, height: 560,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${GM.inkGold}18 0%, transparent 65%)`,
            pointerEvents: "none",
          }}/>

          {/* The window (or uploaded tapestry art) */}
          <div style={{ animation: "bootRise 1.4s ease-out" }}>
            {resolve(BOOT_ASSETS.prologueArt) ? (
              <img src={resolve(BOOT_ASSETS.prologueArt)} alt=""
                   style={{
                     maxWidth: 420,
                     maxHeight: "48vh",
                     filter: `drop-shadow(0 0 30px ${GM.inkGold}44)`,
                     animation: "candleFlicker 4.2s ease-in-out infinite",
                   }}/>
            ) : (
              <StainedGlassWindow size={280}/>
            )}
          </div>

          {/* The illuminated lines */}
          <div style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 10,
            maxWidth: 620,
            textAlign: "center",
          }}>
            {LORE_LINES.map((line, i) => (
              <div key={i} style={{
                fontFamily: GM.font,
                fontSize: 19,
                fontStyle: "italic",
                letterSpacing: 2.5,
                lineHeight: 1.5,
                color: GM.vellum,
                opacity: 0,
                textShadow: `0 0 16px ${GM.inkGold}44`,
                animation: `loreLineIn 1.1s ease-out ${line.delay}s forwards`,
              }}>
                {line.text}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ═══════════ BEAT 3 — KRUSH SCENE ═══════════ */}
      {/* Three-act narration:
            Act A (0–2.6s):  "from the Unstable World of"
                             swirls + drips fade in around the words
            Act B (2.6–3.2s): screen shatters — flash + crack web,
                             narration breaks apart, CAMERA SHAKES
            Act C (3.2–6.5s): KRUSH diamond slams in and reforms,
                             shard embers drift off the impact */}
      {phase === "krush" && (
        <div style={{
          position: "absolute",
          inset: 0,
          background: "#000",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          animation: `
            bootFadeIn 0.6s ease-out,
            krushCamShake 0.5s ease-out 2.6s
          `,
        }}>
          {/* ── Background swirls — slow rotating ink clouds ── */}
          <svg width="100%" height="100%" viewBox="0 0 1000 1000" preserveAspectRatio="xMidYMid slice"
               style={{ position: "absolute", inset: 0, opacity: 0.55 }}>
            <defs>
              <radialGradient id="krushInk1" cx="50%" cy="50%">
                <stop offset="0%"  stopColor="#fff" stopOpacity="0.0"/>
                <stop offset="60%" stopColor="#fff" stopOpacity="0.18"/>
                <stop offset="100%" stopColor="#fff" stopOpacity="0"/>
              </radialGradient>
              <radialGradient id="krushInk2" cx="50%" cy="50%">
                <stop offset="0%"  stopColor="#fff" stopOpacity="0.0"/>
                <stop offset="50%" stopColor="#fff" stopOpacity="0.10"/>
                <stop offset="100%" stopColor="#fff" stopOpacity="0"/>
              </radialGradient>
            </defs>
            <g style={{ animation: "krushSwirl 7s ease-out forwards", transformOrigin: "300px 300px" }}>
              <ellipse cx="300" cy="300" rx="280" ry="180" fill="url(#krushInk1)"/>
            </g>
            <g style={{ animation: "krushSwirl 8s ease-out 0.5s forwards", transformOrigin: "700px 700px" }}>
              <ellipse cx="700" cy="700" rx="320" ry="200" fill="url(#krushInk2)"/>
            </g>
            <g style={{ animation: "krushSwirl 6.5s ease-out 1s forwards", transformOrigin: "750px 250px" }}>
              <ellipse cx="750" cy="250" rx="220" ry="140" fill="url(#krushInk1)"/>
            </g>
          </svg>

          {/* ── Drip streaks — vertical white lines falling ── */}
          {Array.from({ length: 18 }, (_, i) => {
            const left = (i * 53 + (i * i * 7) % 40) % 100;
            const delay = (i * 0.18) % 3.5;
            const dur = 2.2 + (i % 5) * 0.3;
            const width = 1 + (i % 3);
            const height = 40 + (i % 7) * 18;
            return (
              <div key={i} style={{
                position: "absolute",
                left: `${left}%`,
                top: 0,
                width: width,
                height: height,
                background: `linear-gradient(180deg, transparent, #fff 30%, #fff 70%, transparent)`,
                opacity: 0.4 + (i % 4) * 0.15,
                animation: `krushDripFall ${dur}s ease-in ${delay}s infinite`,
                pointerEvents: "none",
              }}/>
            );
          })}

          {/* ── ACT A — narration: "from the Unstable World of" ── */}
          <div style={{
            position: "absolute",
            top: "50%", left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 4,
            display: "flex",
            gap: 14,
            flexWrap: "wrap",
            justifyContent: "center",
            maxWidth: "92vw",
            pointerEvents: "none",
          }}>
            {[
              { word: "from",     delay: 0.2,  sx: -120, sy: -30, sr: -18 },
              { word: "the",      delay: 0.55, sx: -60,  sy: 50,  sr: 12 },
              { word: "Unstable", delay: 0.95, sx: 90,   sy: -40, sr: 20 },
              { word: "World",    delay: 1.45, sx: 140,  sy: 30,  sr: -15 },
              { word: "of",       delay: 1.85, sx: -90,  sy: 60,  sr: 22 },
            ].map((w, i) => (
              <span key={i} style={{
                fontFamily: GM.font,
                fontSize: 26,
                fontStyle: "italic",
                letterSpacing: 3,
                color: "#fff",
                opacity: 0,
                textShadow: "0 0 18px rgba(255,255,255,0.45)",
                "--shx": `${w.sx}px`,
                "--shy": `${w.sy}px`,
                "--shr": `${w.sr}deg`,
                animation: `
                  narrationWordIn 0.55s ease-out ${w.delay}s forwards,
                  narrationShatter 0.55s cubic-bezier(0.5,0,0.75,0) 2.6s forwards
                `,
                display: "inline-block",
              }}>
                {w.word}
              </span>
            ))}
          </div>

          {/* ── ACT B — shatter flash + crack web ── */}
          <div style={{
            position: "absolute",
            inset: 0,
            background: "#fff",
            opacity: 0,
            zIndex: 6,
            pointerEvents: "none",
            animation: "krushFlash 0.45s ease-out 2.6s forwards",
          }}/>
          <svg width="100%" height="100%" viewBox="-500 -500 1000 1000" preserveAspectRatio="xMidYMid meet"
               style={{
                 position: "absolute",
                 inset: 0,
                 zIndex: 5,
                 opacity: 0,
                 animation: "krushCrackWeb 0.9s ease-out 2.7s forwards",
               }}>
            {Array.from({ length: 14 }, (_, i) => {
              const angle = (i * Math.PI * 2) / 14 + (i % 2) * 0.15;
              const x = Math.cos(angle) * 600;
              const y = Math.sin(angle) * 600;
              const mx = Math.cos(angle) * 200;
              const my = Math.sin(angle) * 200;
              const branchAngle = angle + (i % 2 === 0 ? 0.4 : -0.4);
              const bx = mx + Math.cos(branchAngle) * 200;
              const by = my + Math.sin(branchAngle) * 200;
              return (
                <g key={i}>
                  <line x1="0" y1="0" x2={x} y2={y}
                        stroke="#fff" strokeWidth={1 + (i % 3) * 0.5}
                        opacity={0.6 + (i % 3) * 0.13}/>
                  <line x1={mx} y1={my} x2={bx} y2={by}
                        stroke="#fff" strokeWidth={0.6}
                        opacity={0.4}/>
                </g>
              );
            })}
          </svg>

          {/* ── Shard embers — glass fragments drifting off the impact ── */}
          {Array.from({ length: 12 }, (_, i) => {
            const angle = (i * Math.PI * 2) / 12;
            const ex = Math.cos(angle) * (110 + (i % 4) * 45);
            const ey = Math.sin(angle) * (90 + (i % 3) * 55) - 60;
            const er = (i % 2 === 0 ? 1 : -1) * (140 + i * 20);
            return (
              <div key={`ember${i}`} style={{
                position: "absolute",
                top: "50%", left: "50%",
                width: 3 + (i % 3) * 2,
                height: 6 + (i % 4) * 3,
                background: "#fff",
                opacity: 0,
                clipPath: "polygon(50% 0%, 100% 60%, 60% 100%, 0% 45%)",
                "--ex": `${ex}px`,
                "--ey": `${ey}px`,
                "--er": `${er}deg`,
                animation: `shardEmber ${1.4 + (i % 3) * 0.4}s ease-out ${3.0 + (i % 5) * 0.12}s forwards`,
                pointerEvents: "none",
                zIndex: 6,
              }}/>
            );
          })}

          {/* ── ACT C — KRUSH wordmark — slams in after shatter ── */}
          <div style={{
            position: "relative",
            zIndex: 7,
            animation: "krushSlamIn 1.4s cubic-bezier(0.2, 0.8, 0.3, 1) 3.2s backwards",
          }}>
            {resolve(BOOT_ASSETS.krushWordmark) ? (
              <div style={{ animation: "krushShake 0.3s ease-out 4.6s" }}>
                <img src={resolve(BOOT_ASSETS.krushWordmark)} alt="Krush"
                     style={{ maxWidth: 460, maxHeight: 360,
                              filter: "drop-shadow(0 0 30px rgba(255,255,255,0.4))" }}/>
              </div>
            ) : (
              <div style={{ animation: "krushShake 0.3s ease-out 4.6s", position: "relative" }}>
                {/* Diamond frame around the word */}
                <svg width="420" height="320" viewBox="0 0 420 320" style={{
                  position: "absolute",
                  top: "50%", left: "50%",
                  transform: "translate(-50%, -50%)",
                  filter: "drop-shadow(0 0 24px rgba(255,255,255,0.3))",
                }}>
                  <polygon points="210,20 400,160 210,300 20,160"
                           fill="none" stroke="#fff" strokeWidth="3" opacity="0.9"/>
                  <polygon points="210,40 380,160 210,280 40,160"
                           fill="none" stroke="#fff" strokeWidth="1" opacity="0.4"/>
                  {/* Cross-cuts (gem facet lines) */}
                  <line x1="210" y1="20" x2="210" y2="300" stroke="#fff" strokeWidth="0.6" opacity="0.25"/>
                  <line x1="20"  y1="160" x2="400" y2="160" stroke="#fff" strokeWidth="0.6" opacity="0.25"/>
                  <line x1="115" y1="90" x2="305" y2="230" stroke="#fff" strokeWidth="0.4" opacity="0.18"/>
                  <line x1="305" y1="90" x2="115" y2="230" stroke="#fff" strokeWidth="0.4" opacity="0.18"/>
                </svg>
                <div style={{
                  width: 420,
                  height: 320,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontFamily: GM.font,
                  fontSize: 76,
                  fontWeight: 900,
                  fontStyle: "italic",
                  letterSpacing: 8,
                  color: "#fff",
                  textShadow: "0 0 20px rgba(255,255,255,0.4), 0 0 40px rgba(255,255,255,0.15)",
                }}>
                  KRUSH
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══════════ BEAT 4 — VOID AWAKENS ═══════════ */}
      {/* The nebula backdrop persists through dash → legendary →
          title so the world never "cuts to black" again. */}
      {(phase === "void" || phase === "dash" || phase === "legendary" || isTitlePhase) && (
        <div style={{
          position: "absolute",
          inset: 0,
          opacity: isTitlePhase ? 0.7 : 1,
          transition: "opacity 1.2s ease",
        }}>
          {/* Deep nebula gradient */}
          <div style={{
            position: "absolute",
            inset: 0,
            background: `
              radial-gradient(ellipse at 25% 30%, rgba(109,40,217,0.30) 0%, transparent 55%),
              radial-gradient(ellipse at 75% 70%, rgba(153,27,27,0.22) 0%, transparent 55%),
              radial-gradient(ellipse at 60% 20%, rgba(14,116,144,0.18) 0%, transparent 50%),
              radial-gradient(ellipse at 50% 50%, rgba(45,27,105,0.30) 0%, transparent 75%),
              ${GM.page}
            `,
          }}/>
          <StarDust count={120}/>

          {/* Shooting star — streaks once during the void beat */}
          {phase === "void" && (
            <div style={{
              position: "absolute",
              top: "12%", left: 0,
              width: 130, height: 2,
              background: "linear-gradient(90deg, transparent, #fff 60%, #fff)",
              borderRadius: 2,
              boxShadow: `0 0 10px #fff, 0 0 24px ${GM.inkGoldBright}`,
              opacity: 0,
              animation: "shootingStar 2.2s ease-in 1.8s forwards",
              pointerEvents: "none",
            }}/>
          )}

          {/* Four ilk glyphs — Crystal Unown-style reveals:
              each appears with an expanding energy ring "utterance" */}
          {phase === "void" && (
            <>
              {[
                { x: 22, y: 28, color: SD.teal,    char: "◇", delay: 0.6 },  // Space
                { x: 78, y: 32, color: SD.purple,  char: "◈", delay: 1.4 },  // Phantom
                { x: 26, y: 72, color: SD.red,     char: "◆", delay: 2.2 },  // Dragon
                { x: 76, y: 70, color: SD.gold,    char: "✦", delay: 3.0 },  // Time
              ].map((ilk, i) => (
                <div key={i} style={{
                  position: "absolute",
                  left: `${ilk.x}%`,
                  top: `${ilk.y}%`,
                  pointerEvents: "none",
                }}>
                  {/* The glyph */}
                  <div style={{
                    fontSize: 38,
                    color: ilk.color,
                    opacity: 0,
                    textShadow: `0 0 24px ${ilk.color}, 0 0 48px ${ilk.color}88`,
                    animation: `ilkIgnite 1.2s ease-out ${ilk.delay}s forwards`,
                  }}>
                    {ilk.char}
                  </div>
                  {/* The utterance — expanding energy ring */}
                  <div style={{
                    position: "absolute",
                    top: "50%", left: "50%",
                    width: 70, height: 70,
                    marginLeft: -35, marginTop: -35,
                    borderRadius: "50%",
                    border: `1.5px solid ${ilk.color}`,
                    opacity: 0,
                    animation: `glyphUtter 1.3s ease-out ${ilk.delay + 0.15}s forwards`,
                  }}/>
                  {/* Second fainter ring, slightly delayed */}
                  <div style={{
                    position: "absolute",
                    top: "50%", left: "50%",
                    width: 70, height: 70,
                    marginLeft: -35, marginTop: -35,
                    borderRadius: "50%",
                    border: `1px solid ${ilk.color}88`,
                    opacity: 0,
                    animation: `glyphUtter 1.5s ease-out ${ilk.delay + 0.4}s forwards`,
                  }}/>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {/* ═══════════ BEAT 5 — THE DASH ═══════════ */}
      {/* Crystal homage: the legendary blurs across the screen
          like Suicune's run, sigils scatter in its wake, then —
          for one heartbeat — its eyes open in the darkness. */}
      {phase === "dash" && (
        <div style={{
          position: "absolute",
          inset: 0,
          zIndex: 3,
          overflow: "hidden",
          pointerEvents: "none",
        }}>
          {/* Speed lines — horizontal streaks flying opposite the run */}
          {Array.from({ length: 9 }, (_, i) => (
            <div key={`sl${i}`} style={{
              position: "absolute",
              top: `${18 + i * 8.5}%`,
              left: 0,
              width: 90 + (i % 4) * 60,
              height: 1 + (i % 2),
              background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.7), transparent)",
              opacity: 0,
              animation: `speedLine ${0.7 + (i % 3) * 0.2}s linear ${0.15 + i * 0.08}s forwards`,
            }}/>
          ))}

          {/* The legendary — motion-blurred blur across the screen */}
          <div style={{
            position: "absolute",
            top: "50%", left: "50%",
            marginTop: -180,
            marginLeft: -110,
            filter: "blur(3px)",
            animation: "legendaryDash 1.7s cubic-bezier(0.45, 0, 0.55, 1) 0.2s both",
          }}>
            {resolve(BOOT_ASSETS.legendarySilhouette) ? (
              <img src={resolve(BOOT_ASSETS.legendarySilhouette)} alt=""
                   style={{ maxWidth: 260, maxHeight: 360 }}/>
            ) : (
              <PlaceholderLegendary size={220}/>
            )}
          </div>
          {/* Afterimage — a fainter trailing echo */}
          <div style={{
            position: "absolute",
            top: "50%", left: "50%",
            marginTop: -180,
            marginLeft: -110,
            filter: "blur(6px)",
            opacity: 0.45,
            animation: "legendaryDash 1.7s cubic-bezier(0.45, 0, 0.55, 1) 0.32s both",
          }}>
            {resolve(BOOT_ASSETS.legendarySilhouette) ? (
              <img src={resolve(BOOT_ASSETS.legendarySilhouette)} alt=""
                   style={{ maxWidth: 260, maxHeight: 360 }}/>
            ) : (
              <PlaceholderLegendary size={220}/>
            )}
          </div>

          {/* Startled sigils — scatter out of the wake */}
          {[
            { x: 35, y: 62, color: SD.teal,   char: "◇", sx: -70, sy: -50, d: 0.6 },
            { x: 55, y: 58, color: SD.red,    char: "◆", sx: 60,  sy: -70, d: 0.85 },
            { x: 47, y: 66, color: SD.purple, char: "◈", sx: -40, sy: 60,  d: 1.05 },
          ].map((s, i) => (
            <div key={`sc${i}`} style={{
              position: "absolute",
              left: `${s.x}%`, top: `${s.y}%`,
              fontSize: 24,
              color: s.color,
              textShadow: `0 0 16px ${s.color}`,
              opacity: 0,
              "--sx": `${s.sx}px`,
              "--sy": `${s.sy}px`,
              animation: `sigilScatter 1s ease-out ${s.d}s forwards`,
            }}>
              {s.char}
            </div>
          ))}

          {/* One heartbeat: the eyes open in the darkness */}
          <div style={{
            position: "absolute",
            top: "50%", left: "50%",
            transform: "translate(-50%, -50%)",
            opacity: 0,
            animation: `dashEyesFlash 0.85s ease-out ${TIMING.dash / 1000 - 0.95}s forwards`,
          }}>
            <JynxiEyes size={130} color={GM.inkGoldBright}/>
          </div>
        </div>
      )}

      {/* ═══════════ BEAT 6 — LEGENDARY RISES ═══════════ */}
      {phase === "legendary" && (
        <>
          {/* Distant lightning — the void flickers behind the rise */}
          <div style={{
            position: "absolute",
            inset: 0,
            zIndex: 1,
            background: `radial-gradient(ellipse at 50% 20%, ${SD.purple}66 0%, transparent 60%)`,
            opacity: 0,
            animation: "voidLightning 3.5s linear 0.4s infinite",
            pointerEvents: "none",
          }}/>

          {/* Ground fog — soft drifting banks at the base */}
          {[0, 1, 2].map(i => (
            <div key={`fog${i}`} style={{
              position: "absolute",
              bottom: `${-4 + i * 5}%`,
              left: `${-15 + i * 12}%`,
              width: "75%",
              height: "22%",
              borderRadius: "50%",
              background: `radial-gradient(ellipse, rgba(109,40,217,0.14) 0%, transparent 70%)`,
              filter: "blur(14px)",
              animation: `fogDrift ${9 + i * 3}s ease-in-out infinite`,
              zIndex: 1,
              pointerEvents: "none",
            }}/>
          ))}

          {/* Eye-ignite flash — one pulse of light as it wakes */}
          <div style={{
            position: "absolute",
            inset: 0,
            zIndex: 2,
            background: `radial-gradient(circle at 50% 42%, ${GM.inkGoldBright}55 0%, transparent 45%)`,
            opacity: 0,
            animation: "eyeIgniteFlash 1.1s ease-out 1.6s forwards",
            pointerEvents: "none",
          }}/>

          <div style={{
            position: "relative",
            zIndex: 2,
            animation: "bootRise 2s ease-out forwards",
          }}>
            <div style={{ animation: "bootBreathe 4s ease-in-out infinite" }}>
              {resolve(BOOT_ASSETS.legendarySilhouette) ? (
                <img src={resolve(BOOT_ASSETS.legendarySilhouette)} alt=""
                     style={{
                       maxWidth: 420,
                       maxHeight: "70vh",
                       filter: `drop-shadow(0 0 50px ${SD.purple}88) drop-shadow(0 0 25px ${GM.inkGold}55)`,
                     }}/>
              ) : (
                <PlaceholderLegendary size={320}/>
              )}
            </div>
          </div>
        </>
      )}

      {/* ═══════════ BEAT 7+8 — TITLE + PRESS ENTER ═══════════ */}
      {isTitlePhase && (
        <>
          {/* God rays — slow-rotating gold beams behind the title */}
          <div style={{
            position: "absolute",
            top: "50%", left: "50%",
            width: "160vmax", height: "160vmax",
            marginLeft: "-80vmax", marginTop: "-80vmax",
            zIndex: 1,
            background: `repeating-conic-gradient(
              from 0deg,
              ${GM.inkGold}14 0deg 7deg,
              transparent 7deg 24deg
            )`,
            maskImage: "radial-gradient(circle, black 0%, transparent 55%)",
            WebkitMaskImage: "radial-gradient(circle, black 0%, transparent 55%)",
            animation: "godRaysSpin 90s linear infinite",
            pointerEvents: "none",
          }}/>

          {/* Ghost legend — Crystal homage: the legendary looms
              faintly behind the wordmark, breathing */}
          <div style={{
            position: "absolute",
            top: "50%", left: "50%",
            transform: "translate(-50%, -62%)",
            zIndex: 1,
            animation: "ghostLoom 7s ease-in-out infinite",
            pointerEvents: "none",
          }}>
            {resolve(BOOT_ASSETS.legendarySilhouette) ? (
              <img src={resolve(BOOT_ASSETS.legendarySilhouette)} alt=""
                   style={{ maxWidth: 520, maxHeight: "82vh" }}/>
            ) : (
              <PlaceholderLegendary size={400}/>
            )}
          </div>

          {/* Gold shockwave ring on title arrival */}
          <div style={{
            position: "absolute",
            top: "50%", left: "50%",
            width: 320, height: 320,
            marginLeft: -160, marginTop: -160,
            borderRadius: "50%",
            border: `2px solid ${GM.inkGoldBright}`,
            boxShadow: `0 0 30px ${GM.inkGold}66`,
            opacity: 0,
            zIndex: 1,
            animation: "titleShockwave 1.3s ease-out 0.3s forwards",
            pointerEvents: "none",
          }}/>

          <div style={{
            position: "relative",
            zIndex: 2,
            textAlign: "center",
            animation: "bootRise 1.4s ease-out forwards",
            padding: "0 30px",
            maxWidth: "92vw",
          }}>
            {/* Top corner flourishes — only at title moment */}
            <div style={{ position: "absolute", top: -60, left: -20, opacity: 0.7 }}>
              <CornerFlourish size={48} color={GM.inkGold}/>
            </div>
            <div style={{ position: "absolute", top: -60, right: -20, opacity: 0.7, transform: "scaleX(-1)" }}>
              <CornerFlourish size={48} color={GM.inkGold}/>
            </div>

            {/* Mascot — left of title (Pokemon Red/Blue style) */}
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 20,
              flexWrap: "wrap",
            }}>
              <div style={{
                width: 180, height: 180,
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "opacity 0.5s",
              }}>
                {resolve(BOOT_ASSETS.mascotCarousel[mascotIdx]) ? (
                  <img key={mascotIdx}
                       src={resolve(BOOT_ASSETS.mascotCarousel[mascotIdx])}
                       alt=""
                       style={{
                         maxWidth: "100%", maxHeight: "100%",
                         filter: `drop-shadow(0 0 18px ${GM.inkGold}66)`,
                         animation: "bootFadeIn 0.5s ease-out",
                       }}/>
                ) : (
                  <div key={mascotIdx} style={{ animation: "bootFadeIn 0.5s ease-out" }}>
                    <PlaceholderMascot size={160} idx={mascotIdx}/>
                  </div>
                )}
              </div>

              {/* Title wordmark */}
              <div>
                {resolve(BOOT_ASSETS.titleWordmark) ? (
                  <img src={resolve(BOOT_ASSETS.titleWordmark)} alt="TERROR"
                       style={{ maxWidth: 480, maxHeight: 200,
                                filter: `drop-shadow(0 0 30px ${GM.inkGold}88)` }}/>
                ) : (
                  <PlaceholderWordmark text="TERROR" size={88}/>
                )}
                <div style={{
                  marginTop: 4,
                  fontSize: 11,
                  letterSpacing: 8,
                  color: GM.vellumDim,
                  fontStyle: "italic",
                }}>
                  the krush universe
                </div>
              </div>
            </div>

            <div style={{ width: "100%", maxWidth: 480, margin: "32px auto 0" }}>
              <OrnateDivider color={GM.inkGold}/>
            </div>

            {/* Press Enter prompt — only on pressEnter phase */}
            {phase === "pressEnter" && (
              <div style={{
                marginTop: 20,
                fontSize: 12,
                letterSpacing: 6,
                color: GM.inkGoldBright,
                animation: "bootPulse 1.6s ease-in-out infinite",
                fontWeight: 700,
              }}>
                ◆ PRESS ENTER ◆
              </div>
            )}
          </div>
        </>
      )}

      {/* ═══════════ CINEMATIC LAYER — always on ═══════════ */}
      <Vignette/>
      <FilmGrain/>
      <Letterbox retracted={isTitlePhase}/>

      {/* Skip hint — small, bottom corner, all phases except final */}
      {phase !== "pressEnter" && (
        <div style={{
          position: "absolute",
          bottom: 16,
          right: 20,
          fontSize: 9,
          letterSpacing: 3,
          color: GM.vellumFaint,
          opacity: 0.6,
          zIndex: 51,
          pointerEvents: "none",
        }}>
          tap or press any key to skip
        </div>
      )}
    </div>
  );
}
